import { createReducer, createActions } from 'reduxsauce';
import { generateUuid } from 'utils/uuid';

/* ------------------- Types and Action Creators -------------- */
const { Types, Creators } = createActions({
  reset: [],
  initializeCart: ['venueId'],
  sendOrder: ['venueId', 'locationId'],
  loadMenuItems: ['venueId', 'forced'],
  setMenuItems: ['venueId', 'menuItems'],
  setMenuCategories: ['venueId', 'categories'],
  setMenuItemsLoading: ['loading'],
  setSendingOrder: ['sending'],
  addItemToCart: ['venueId', 'quantity', 'item', 'modifiers'],
  editItemInCart: ['uuid', 'quantity', 'modifiers', 'groupId'],
  deleteItemFromCart: ['uuid'],
  resetCart: [],
});

export const OrderTypes = Types;

export default Creators;

/* ---------------- Selectors -------------- */
const selectMenuItem = (venueId, itemId) => (state) => {
  const items = state.order.menuItems[venueId] || [];
  return items.find((item) => item.id === itemId);
};

export const OrderSelectors = {
  selectIsLoaded: (venueId) => (state) => !!state.order.menuItems[venueId],
  selectMenuItems: (venueId) => (state) => state.order.menuItems[venueId] || [],
  selectMenuItemsLoading: (state) => state.order.loading,
  selectSending: (state) => state.order.sending,
  selectMenuItemsForCategory: (venueId, categoryId) => (state) => {
    const items = state.order.menuItems[venueId] || [];
    return items.filter((item) => item.category_id === categoryId);
  },
  selectTabCategories: (venueId) => (state) => {
    const categories = state.order.categories[venueId] || [];

    return categories.filter((c) => c.display);
  },
  selectAllItemsWithCategory: (venueId) => (state) => {
    const result = [...(state.order.categories[venueId] || [])].map((r) => ({
      ...r,
      kind: 'category',
    }));
    const menuItems = state.order.menuItems[venueId] || [];
    (state.order.categories[venueId] || []).forEach((cat) => {
      const catItems = menuItems
        .filter((item) => item.customCategoryId === cat.id)
        .map((i) => ({ ...i, kind: 'menu-item' }))
        .sort((a1, a2) => a1.order - a2.order);
      const index = result.findIndex((r) => r.id === cat.id);
      result.splice(index + 1, 0, ...catItems);
    });

    return result;
  },
  selectMenuItem,
  selectCartItems: (venueId) => (state) => {
    return state.order.cart.filter((c) => c.venueId === venueId);
  },
  selectCartSubtotal: (venueId) => (state) => {
    return state.order.cart
      .filter((c) => c.venueId === venueId)
      .reduce((prev, cartItem) => {
        const menuItem = selectMenuItem(venueId, cartItem.item.id)(state);
        const modifierTotal = cartItem.modifiers.reduce(
          (prevModifier, modifier) =>
            prevModifier + modifier.item.price * cartItem.quantity,
          0
        );

        if (menuItem) {
          return prev + cartItem.quantity * menuItem.price + modifierTotal;
        }

        return prev;
      }, 0);
  },
};

/* ---------------- Initial State -------------- */
export const INITIAL_STATE = {
  menuItems: {},
  categories: {},
  cart: [],
  sending: false,
  loading: false,
};

/* ---------------- Reducers -------------- */
export const setMenuItems = (state, { venueId, menuItems }) => ({
  ...state,
  menuItems: {
    ...state.menuItems,
    [venueId]: menuItems,
  },
});

export const setMenuCategories = (state, { venueId, categories }) => ({
  ...state,
  categories: {
    ...state.categories,
    [venueId]: categories,
  },
});

export const setMenuItemsLoading = (state, { loading }) => ({
  ...state,
  loading,
});
export const setSendingOrder = (state, { sending }) => ({
  ...state,
  sending,
});
export const addItemToCart = (
  state,
  { venueId, quantity, item, modifiers }
) => ({
  ...state,
  cart: [
    ...state.cart,
    { uuid: generateUuid(), venueId, quantity, item, modifiers },
  ],
});
export const editItemInCart = (
  state,
  { uuid, quantity, modifiers, groupId }
) => ({
  ...state,
  cart: state.cart.map((c) =>
    c.uuid === uuid
      ? {
        ...c,
        groupId,
        quantity,
        modifiers,
      }
      : c
  ),
});
export const deleteItemFromCart = (state, { uuid }) => ({
  ...state,
  cart: state.cart.filter((c) => c.uuid !== uuid),
});
export const resetCart = (state) => ({
  ...state,
  cart: [],
});
export const reset = () => ({ ...INITIAL_STATE });

export const reducer = createReducer(INITIAL_STATE, {
  [Types.SET_MENU_ITEMS]: setMenuItems,
  [Types.SET_MENU_CATEGORIES]: setMenuCategories,
  [Types.SET_MENU_ITEMS_LOADING]: setMenuItemsLoading,
  [Types.SET_SENDING_ORDER]: setSendingOrder,
  [Types.ADD_ITEM_TO_CART]: addItemToCart,
  [Types.EDIT_ITEM_IN_CART]: editItemInCart,
  [Types.DELETE_ITEM_FROM_CART]: deleteItemFromCart,
  [Types.RESET_CART]: resetCart,
  [Types.RESET]: reset,
});
